/**
 * Authentication Wrapper Component for Heyward
 * Uses Clerk for authentication with TOS gate
 */

import React, { createContext, useContext, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ClerkProvider,
  SignIn,
  SignUp,
  Waitlist,
  useAuth as useClerkAuth,
  useUser
} from '@clerk/react';
import posthog from 'posthog-js';
import { motion } from 'framer-motion';
import ComplianceGate from './ComplianceGate';
import LandingPage from '../../pages/LandingPage';
import '../../styles/auth-mobile-readable.css';
import { authFetch } from '../../utils/authFetch';
import { clearStoredReferralCode, readStoredReferralCode } from '../../utils/referralCapture';
import {
  attributionStorageAction,
  clearAttributionAttempted,
  markAttributionAttempted,
  readAttributionAttempted,
  shouldAttemptAttribution,
} from '../../utils/referralAttribution';

// Get Clerk publishable key from environment
const clerkPubKey = import.meta.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Check for development mode
const isDevelopment = import.meta.env.DEV;
const devModeEnabled = import.meta.env.REACT_APP_DEV_MODE === 'true';

// Runtime escape hatch: setting this localStorage key flips AuthWrapper
// from the bypass DevAuthProvider into the real ClerkProvider so a
// developer can sign in with their actual prod account locally without
// touching REACT_APP_DEV_MODE and restarting the dev server. Set by the
// sidebar's Sign Out button when running in dev. Clear it to return to
// the dev-user-id bypass (or sign out from Clerk normally).
const DEV_FORCE_CLERK_KEY = 'heyward_dev_force_clerk_auth';
function shouldUseDevAuth() {
  if (!isDevelopment || !devModeEnabled) return false;
  try { return window.localStorage.getItem(DEV_FORCE_CLERK_KEY) !== '1'; }
  catch { return true; }
}

// Auth Context for compatibility with existing code
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Loading component
const AuthLoading = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold text-gray-700 mb-2">Heyward</h2>
      <p className="text-gray-500">Initializing authentication...</p>
    </motion.div>
  </div>
);

// Development Auth Provider (bypasses Clerk)
// Test hooks (localStorage, take effect on reload):
//   heyward_dev_role = 'user'   → isAdmin:false, so the app renders exactly
//                                 what a normal production user sees
//                                 (admin-gated surfaces, admin-sees-all
//                                 queries). Remove the key for admin.
//   heyward_dev_user_id = '<id>' → impersonate a different user id, e.g. a
//                                 fresh id to walk the true new-user flow
//                                 against an empty backend state.
const DevAuthProvider = ({ children }) => {
  let devRole = null, devUserId = null;
  try {
    devRole = window.localStorage.getItem('heyward_dev_role');
    devUserId = window.localStorage.getItem('heyward_dev_user_id');
  } catch { /* storage unavailable — default to admin dev user */ }
  const value = {
    user: {
      username: 'dev-user',
      userId: devUserId || 'dev-user-id',
      attributes: {
        email: 'developer@heyward.local',
        given_name: 'Dev',
        family_name: 'User'
      }
    },
    authToken: 'dev-token-123',
    loading: false,
    signOut: () => {},
    getAuthToken: () => Promise.resolve('dev-token-123'),
    isAuthenticated: true,
    isAdmin: devRole !== 'user',
    isDevelopment: true
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Clerk Auth Provider — bridges Clerk to the existing useAuth interface
const ClerkAuthProvider = ({ children }) => {
  const { isLoaded, isSignedIn, signOut, getToken, userId } = useClerkAuth();
  const { user } = useUser();
  const referralAttemptInFlight = useRef(false);

  const handleSignOut = useCallback(() => signOut(), [signOut]);
  const getAuthToken = useCallback(() => getToken(), [getToken]);

  // Identify user in PostHog when authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn && userId) {
      posthog.identify(userId, {
        // Real-world identity so PostHog shows recognizable people, not opaque IDs.
        // $name / $email are PostHog's reserved person properties shown across the UI.
        $name:
          user?.fullName ||
          [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
          undefined,
        $email: user?.primaryEmailAddress?.emailAddress,
        username: user?.username || undefined,
        first_name: user?.firstName || undefined,
        last_name: user?.lastName || undefined,
        avatar_url: user?.imageUrl || undefined,
        created_at: user?.createdAt ? new Date(user.createdAt).toISOString() : undefined,
        is_admin: user?.publicMetadata?.role === 'admin',
      });
    } else if (isLoaded && !isSignedIn) {
      posthog.reset();
    }
  }, [isLoaded, isSignedIn, userId, user]);

  // First authenticated load applies any captured referral exactly once.
  // The attempted flag is written before the request so re-renders and React
  // strict effects cannot duplicate the POST.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) return;

    const storedCode = readStoredReferralCode();
    const attempted = referralAttemptInFlight.current || readAttributionAttempted();
    if (!shouldAttemptAttribution({ isSignedIn, storedCode, attempted })) return;

    referralAttemptInFlight.current = true;
    markAttributionAttempted();

    void authFetch('/api/v1/referrals/attribute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: storedCode }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Referral attribution returned HTTP ${response.status}`);
        }

        const result = await response.json();
        const action = attributionStorageAction({ status: result?.status });
        if (action.clearStoredCode) {
          clearStoredReferralCode();
          return;
        }

        console.error('Referral attribution returned an unexpected business outcome.', result);
        if (action.clearAttempted) {
          clearAttributionAttempted();
          referralAttemptInFlight.current = false;
        }
      })
      .catch((error) => {
        console.error('Referral attribution could not be completed.', error);
        const action = attributionStorageAction({ error: true });
        if (action.clearAttempted) {
          clearAttributionAttempted();
          referralAttemptInFlight.current = false;
        }
      });
  }, [isLoaded, isSignedIn, userId, user]);

  const value = useMemo(() => ({
    user: user ? {
      username: user.username || user.primaryEmailAddress?.emailAddress,
      userId: userId,
      attributes: {
        email: user.primaryEmailAddress?.emailAddress,
        given_name: user.firstName,
        family_name: user.lastName
      },
      publicMetadata: user.publicMetadata || {}
    } : null,
    authToken: null, // always null — use getAuthToken() for async token retrieval
    loading: !isLoaded || (isSignedIn && !user),
    signOut: handleSignOut,
    getAuthToken,
    isAuthenticated: !!isSignedIn,
    isAdmin: user?.publicMetadata?.role === 'admin',
    isDevelopment: false
  }), [user, userId, isLoaded, isSignedIn, handleSignOut, getAuthToken]);


  if (!isLoaded) {
    return <AuthLoading />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Clerk appearance theme — matches the Heyward landing palette
// (stone-800 ink, neutral surfaces, no blue). Clerk's own header is
// hidden so the editorial serif headline above the card is the only
// title and the typography stays on system.
const signInAppearance = {
  layout: {
    termsPageUrl: `${window.location.origin}/terms`,
    privacyPageUrl: `${window.location.origin}/privacy`,
    socialButtonsPlacement: 'top',
    socialButtonsVariant: 'blockButton',
  },
  variables: {
    colorPrimary: '#292524',          // stone-800
    colorText: '#171717',             // ink (neutral-900)
    colorTextSecondary: '#737373',    // ink-mute (neutral-500)
    colorBackground: '#ffffff',
    colorInputBackground: '#fafafa',
    colorInputText: '#171717',
    colorDanger: '#dc2626',
    colorSuccess: '#10b981',
    colorNeutral: '#171717',
    borderRadius: '10px',
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    fontSize: '14px',
  },
  elements: {
    rootBox: 'mx-auto',
    // Hide Clerk's built-in "Sign in to Heyward" header — replaced by the
    // editorial serif headline rendered above the card.
    header: 'hidden',
    // Keep the footer visible so Clerk's built-in "Don't have an account?
    // Sign up" link in `footerAction` works. To kill the "Secured by
    // Clerk" branding, use the DASHBOARD toggle:
    //   Clerk Dashboard → Customization → "Powered by Clerk" → OFF
    // (available on the paid plan; cleaner than CSS-hiding since Clerk's
    // internal class names change between versions and a CSS hack
    // accidentally killed the sign-up link in commit ac038445).
    card: 'shadow-none border border-neutral-200 rounded-[14px]',
    button: 'min-h-12 text-base',
    formButtonPrimary: 'min-h-12 bg-stone-800 hover:bg-stone-900 text-white text-base font-medium rounded-[10px] normal-case tracking-normal',
    socialButtonsBlockButton: 'min-h-12 border-neutral-200 hover:bg-neutral-50 text-base rounded-[10px] normal-case tracking-normal',
    formFieldInput: 'min-h-12 border-neutral-200 focus:border-stone-800 focus:ring-stone-200 rounded-[10px] text-base',
    formFieldInputShowPasswordButton: 'min-h-11 min-w-11',
    formFieldLabel: 'text-neutral-700 text-[15px] font-medium',
    formFieldErrorText: 'text-sm leading-relaxed',
    footerActionLink: 'inline-flex min-h-11 items-center text-stone-800 hover:text-stone-900 text-[15px] font-medium',
    footerActionText: 'text-neutral-500 text-[15px]',
    dividerLine: 'bg-neutral-200',
    dividerText: 'text-neutral-400',
    formResendCodeLink: 'inline-flex min-h-11 items-center text-stone-800 hover:text-stone-900 text-[15px]',
    identityPreviewEditButton: 'inline-flex min-h-11 items-center text-stone-800 hover:text-stone-900 text-[15px]',
  },
};

// Sign in landing — typography + neutral surface match the marketing
// landing page (Inter + Instrument Serif, #fafafa background with the
// same rose/indigo hero-bg radial gradients). The brand wordmark in
// the top-left is a quiet link back to /.
const SignInPage = () => (
  <div
    className="auth-shell"
    style={{
      minHeight: '100dvh',
      background: '#fafafa',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      color: '#262626',
    }}
  >
    {/* Subtle brand-tone radial gradients, mirroring .landing .hero-bg. */}
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: '-10% -10% 0 -10%',
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 50% 40% at 80% 30%, rgba(244,63,94,0.06), transparent 60%), '
          + 'radial-gradient(ellipse 60% 50% at 20% 70%, rgba(99,102,241,0.05), transparent 60%)',
        filter: 'blur(20px)',
      }}
    />

    {/* Brand wordmark, top-left — same Inter semibold as the landing nav. */}
    <a
      className="auth-shell__brand"
      href="/"
      style={{
        position: 'absolute', top: 24, left: 32, zIndex: 1,
        fontFamily: '"Inter", system-ui, sans-serif',
        fontWeight: 600, fontSize: 17, letterSpacing: '-0.02em',
        color: '#171717', textDecoration: 'none',
      }}
    >
      Heyward
    </a>

    {/* Centered column */}
    <div
      className="auth-shell__column"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '80px 20px 40px',
      }}
    >
      <motion.div
        className="auth-shell__panel"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: '100%', maxWidth: 440, margin: 'auto 0' }}
      >
        {/* Mono eyebrow — same conic-gradient dot as the landing vert-badge. */}
        <div
          className="auth-shell__eyebrow"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            margin: '0 auto 18px',
            width: '100%', justifyContent: 'center',
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10.5, letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#737373',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 7, height: 7, borderRadius: '50%',
              background: 'conic-gradient(from 180deg at 50% 50%, #f43f5e, #6366f1, #f43f5e)',
            }}
          />
          Vertical AI agent · Australian property
        </div>

        {/* Editorial headline */}
        <h1
          className="auth-shell__headline"
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(30px, 3.6vw, 38px)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: '#171717',
            textAlign: 'center',
            margin: '0 0 8px',
          }}
        >
          Sign in to <span style={{ fontStyle: 'italic' }}>continue.</span>
        </h1>
        <p
          className="auth-shell__subhead"
          style={{
            textAlign: 'center',
            color: '#525252',
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: '0 0 28px',
            maxWidth: 38 + 'ch',
            marginLeft: 'auto', marginRight: 'auto',
          }}
        >
          New here? Create your account below.
        </p>

        <SignIn
          appearance={signInAppearance}
          routing="hash"
          fallbackRedirectUrl={window.location.origin}
          forceRedirectUrl={window.location.origin}
          signUpFallbackRedirectUrl={window.location.origin}
          signUpForceRedirectUrl={window.location.origin}
          signUpUrl="/sign-up"
        />

        {/* Privacy/Terms now render inside Clerk's footer (configured via
            signInAppearance.layout.{termsPageUrl, privacyPageUrl}) along
            with the "Don't have an account? Sign up" link. */}

        {/* Dev-only escape hatch — only renders on localhost when the
            sidebar Sign Out has flipped us into Clerk mode. Lets the dev
            go back to the dev-user-id bypass without restarting CRA. */}
        {typeof window !== 'undefined' &&
         /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) &&
         (() => { try { return window.localStorage.getItem(DEV_FORCE_CLERK_KEY) === '1'; } catch { return false; } })() && (
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => {
                try { window.localStorage.removeItem(DEV_FORCE_CLERK_KEY); } catch {}
                window.location.reload();
              }}
              style={{
                fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
                fontSize: 10, letterSpacing: '0.12em',
                color: '#737373', background: 'transparent',
                border: 'none', cursor: 'pointer', padding: '4px 8px',
                textTransform: 'uppercase', textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
              }}
            >
              ← use dev account
            </button>
          </div>
        )}
      </motion.div>
    </div>
  </div>
);

// Sign-up landing — mirrors SignInPage shell (same background, wordmark,
// editorial headline + mono eyebrow) but renders Clerk's <SignUp>
// component instead of <SignIn>. Requires the prod Clerk app in Public
// sign-up mode (User & authentication → Restrictions); in Waitlist mode
// Clerk renders its restricted-signup state here instead.
const SignUpPage = () => (
  <div
    className="auth-shell"
    style={{
      minHeight: '100dvh',
      background: '#fafafa',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      color: '#262626',
    }}
  >
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: '-10% -10% 0 -10%',
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 50% 40% at 80% 30%, rgba(244,63,94,0.06), transparent 60%), '
          + 'radial-gradient(ellipse 60% 50% at 20% 70%, rgba(99,102,241,0.05), transparent 60%)',
        filter: 'blur(20px)',
      }}
    />

    <a
      className="auth-shell__brand"
      href="/"
      style={{
        position: 'absolute', top: 24, left: 32, zIndex: 1,
        fontFamily: '"Inter", system-ui, sans-serif',
        fontWeight: 600, fontSize: 17, letterSpacing: '-0.02em',
        color: '#171717', textDecoration: 'none',
      }}
    >
      Heyward
    </a>

    <div
      className="auth-shell__column"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '80px 20px 40px',
      }}
    >
      <motion.div
        className="auth-shell__panel"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: '100%', maxWidth: 440, margin: 'auto 0' }}
      >
        <div
          className="auth-shell__eyebrow"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            margin: '0 auto 18px',
            width: '100%', justifyContent: 'center',
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10.5, letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#737373',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 7, height: 7, borderRadius: '50%',
              background: 'conic-gradient(from 180deg at 50% 50%, #f43f5e, #6366f1, #f43f5e)',
            }}
          />
          Vertical AI agent · Australian property
        </div>

        <h1
          className="auth-shell__headline"
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(30px, 3.6vw, 38px)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: '#171717',
            textAlign: 'center',
            margin: '0 0 8px',
          }}
        >
          Create your <span style={{ fontStyle: 'italic' }}>account.</span>
        </h1>
        <p
          className="auth-shell__subhead"
          style={{
            textAlign: 'center',
            color: '#525252',
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: '0 0 28px',
            maxWidth: 40 + 'ch',
            marginLeft: 'auto', marginRight: 'auto',
          }}
        >
          Free to start. No card required.
        </p>

        <SignUp
          appearance={signInAppearance}
          routing="hash"
          fallbackRedirectUrl={window.location.origin}
          forceRedirectUrl={window.location.origin}
          signInUrl="/sign-in"
          waitlistUrl="/waitlist"
        />
      </motion.div>
    </div>
  </div>
);

// Clerk's restricted sign-up CTA otherwise opens the waitlist in an
// account-portal modal, outside Heyward's shell and appearance overrides.
// A mounted Waitlist keeps the complete flow branded, readable and routable.
const WaitlistPage = () => (
  <div
    className="auth-shell"
    style={{
      minHeight: '100dvh',
      background: '#fafafa',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      color: '#262626',
    }}
  >
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: '-10% -10% 0 -10%',
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 50% 40% at 80% 30%, rgba(244,63,94,0.06), transparent 60%), '
          + 'radial-gradient(ellipse 60% 50% at 20% 70%, rgba(99,102,241,0.05), transparent 60%)',
        filter: 'blur(20px)',
      }}
    />

    <a
      className="auth-shell__brand"
      href="/"
      style={{
        position: 'absolute', top: 24, left: 32, zIndex: 1,
        fontFamily: '"Inter", system-ui, sans-serif',
        fontWeight: 600, fontSize: 17, letterSpacing: '-0.02em',
        color: '#171717', textDecoration: 'none',
      }}
    >
      Heyward
    </a>

    <div
      className="auth-shell__column"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '80px 20px 40px',
      }}
    >
      <motion.div
        className="auth-shell__panel"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: '100%', maxWidth: 440, margin: 'auto 0' }}
      >
        <div
          className="auth-shell__eyebrow"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            margin: '0 auto 18px',
            width: '100%', justifyContent: 'center',
            fontFamily: '"JetBrains Mono", ui-monospace, Menlo, monospace',
            fontSize: 10.5, letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#737373',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 7, height: 7, borderRadius: '50%',
              background: 'conic-gradient(from 180deg at 50% 50%, #f43f5e, #6366f1, #f43f5e)',
            }}
          />
          Vertical AI agent · Australian property
        </div>

        <h1
          className="auth-shell__headline"
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(30px, 3.6vw, 38px)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: '#171717',
            textAlign: 'center',
            margin: '0 0 8px',
          }}
        >
          Join the <span style={{ fontStyle: 'italic' }}>waitlist.</span>
        </h1>
        <p
          className="auth-shell__subhead"
          style={{
            textAlign: 'center',
            color: '#525252',
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: '0 0 28px',
            maxWidth: 42 + 'ch',
            marginLeft: 'auto', marginRight: 'auto',
          }}
        >
          Enter your email address and we’ll let you know when your spot is ready.
        </p>

        <Waitlist
          appearance={signInAppearance}
          signInUrl="/sign-in"
          afterJoinWaitlistUrl={window.location.origin}
        />
      </motion.div>
    </div>
  </div>
);

// Access gate (ADR-025 §6.4): TOS + personal-use declaration + eligibility
// state, server-driven and fail-closed. Applies in both the dev and Clerk
// branches because both render <TOSGate> around the app.
const TOSGate = ComplianceGate;

// AuthGate — replaces <SignedIn>/<SignedOut> (removed in @clerk/react v6).
// Must live inside <ClerkProvider> so useClerkAuth() is available.
const AuthGate = ({ children }) => {
  const { isSignedIn, isLoaded } = useClerkAuth();
  if (!isLoaded) return <AuthLoading />;
  if (isSignedIn) {
    return (
      <ClerkAuthProvider>
        <TOSGate>{children}</TOSGate>
      </ClerkAuthProvider>
    );
  }
  return <SignedOutGate />;
};

// SignedOutGate — when an unauthenticated visitor lands on the site, route
// them based on path:
//   - `/` or `/landing` → Heyward marketing landing (LandingPage)
//   - `/sign-up`        → Clerk <SignUp> (self-serve; `/waitlist` kept as an
//                         alias because the URL is in the sitemap and in
//                         links shared during the waitlist era)
//   - everything else   → Clerk SignInPage (so deep links like /portfolio
//                         force a sign-in instead of dropping the visitor
//                         onto marketing with no context).
// This sits inside <ClerkProvider> so any Clerk hooks below it keep
// working; LandingPage doesn't use Clerk hooks itself.
const SignedOutGate = () => {
  const { pathname } = useLocation();
  if (pathname === '/' || pathname === '/landing') return <LandingPage />;
  if (pathname === '/sign-up') return <SignUpPage />;
  if (pathname === '/waitlist') return <WaitlistPage />;
  return <SignInPage />;
};

// Main Auth Wrapper Component
const AuthWrapper = ({ children }) => {
  // Use development auth provider if in dev mode AND the runtime override
  // hasn't been flipped (set by sidebar Sign Out in dev to route through
  // Clerk for real-account login without a server restart).
  if (shouldUseDevAuth()) {
    return (
      <DevAuthProvider>
        <TOSGate>
          <div className="min-h-screen" style={{ background: '#fafafa' }}>
            <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 md:sticky md:top-0 z-40">
              <div className="flex items-center justify-between gap-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-700">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Development Mode:</strong> Authentication is bypassed.
                      Click <em>Sign Out</em> in the sidebar to log in with your real account, or set REACT_APP_DEV_MODE=false to disable this banner.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {children}
          </div>
        </TOSGate>
      </DevAuthProvider>
    );
  }

  // Check for Clerk key
  if (!clerkPubKey) {
    console.error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY');
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8">
          <h1 className="text-xl font-bold text-red-600">Configuration Error</h1>
          <p className="text-gray-600 mt-2">Missing Clerk publishable key. Set REACT_APP_CLERK_PUBLISHABLE_KEY in your environment.</p>
        </div>
      </div>
    );
  }

  // Production authentication with Clerk
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      signInFallbackRedirectUrl={window.location.origin}
      signInForceRedirectUrl={window.location.origin}
      signUpFallbackRedirectUrl={window.location.origin}
      signUpForceRedirectUrl={window.location.origin}
      afterSignOutUrl={window.location.origin}
    >
      <AuthGate>{children}</AuthGate>
    </ClerkProvider>
  );
};

export default AuthWrapper;
